import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from '@app/App';
import '@core/config/env';
import '@theme/print.css';
// ADR-061 design system, loaded app-wide.
//
// This was previously imported by individual tab components, which meant
// any screen that had NOT opted in rendered `tw:`-prefixed classes with
// no CSS behind them at all — the shared patient search looked correct
// on the Registration dashboard and collapsed to an unstyled input on
// the Appointments page, because only the former imported it.
//
// Loading it at the root is safe: `styles/tailwind.css` imports only
// Tailwind's `theme` and `utilities` layers and deliberately never
// `preflight`, so it adds design tokens and prefixed utilities without
// resetting box-sizing, margins or typography anywhere. MUI's own
// `CssBaseline` keeps doing that job for every not-yet-migrated screen.
import './styles/tailwind.css';

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

// A tab left open across a deploy still holds the OLD index.html's lazy-page
// chunk references in memory; every route in `routes/lazy-pages.ts` is its
// own content-hashed chunk, and the old release (with the old hashes) is
// pruned by `deploy.sh` shortly after. Clicking a not-yet-loaded page then
// tries to fetch a chunk file that no longer exists — Vite's dynamic
// `import()` rejects, React has no boundary for it, and nothing renders
// until the user manually refreshes. Vite fires `vite:preloadError` on the
// window for exactly this case; the documented recovery is a one-time
// reload to pick up the current index.html and valid chunk hashes. Guarded
// by sessionStorage so a genuinely broken deploy can't reload-loop the tab;
// the guard clears once this boot succeeds, so a *later* deploy still gets
// its own single retry.
const PRELOAD_ERROR_RELOAD_KEY = 'nexao:reloaded-after-preload-error';
window.addEventListener('vite:preloadError', () => {
  if (sessionStorage.getItem(PRELOAD_ERROR_RELOAD_KEY)) return;
  sessionStorage.setItem(PRELOAD_ERROR_RELOAD_KEY, '1');
  window.location.reload();
});

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

/**
 * CLEARED ONLY ONCE THE BOOT HAS ACTUALLY SUCCEEDED — not on the next line.
 *
 * This used to run synchronously right after `render()`, which returns long
 * before any lazy chunk has resolved. So the guard was gone by the time the
 * stale chunk failed again, and the "one-time" retry became a reload loop: the
 * tab sat on "Loading…" and refreshed forever, which is precisely the failure
 * the guard exists to prevent.
 *
 * `window.load` is the honest signal: it fires after the document and its
 * subresources have finished, so reaching it means this boot got far enough to
 * be worth trusting. A later deploy still gets its own single retry.
 */
if (document.readyState === 'complete') {
  sessionStorage.removeItem(PRELOAD_ERROR_RELOAD_KEY);
} else {
  window.addEventListener('load', () => sessionStorage.removeItem(PRELOAD_ERROR_RELOAD_KEY), { once: true });
}
